import React, { ChangeEvent } from "react";
import styles from "./styles.module.scss";
import { UIRadioBtn, UIText } from "src/components/uIKit";
import { useAppSelector } from "src/hooks/useRedux";
import classNames from "classnames";
import {
  formatDateRange,
  formatDestination,
  formatNumberInHebrew,
  formatNumberWithNoun,
  truncateText,
} from "src/utils/utils";
import { HebrowNouns } from "src/utils/enums";
import { useIsMobile } from "src/hooks/useIsMobile";
import { Tooltip } from "antd";

interface CardItemProps {
  passengers: string;
  dates: string;
  index: number;
  isChecked: boolean;
  destination: string;
  onCheckboxChange: (
    index: number,
    event: ChangeEvent<HTMLInputElement>
  ) => void;
}

const PolicyItem: React.FC<CardItemProps> = ({
  passengers,
  dates,
  index,
  isChecked,
  destination,
  onCheckboxChange,
}) => {
  const isMobile = useIsMobile();
  const { strings } = useAppSelector((state) => state.app);

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    onCheckboxChange(index, event);
  };

  const policyAriaLabel = (
    index: number,
    destination: string,
    passengers: string,
    dates: string
  ) => {
    return `${formatNumberWithNoun(index, HebrowNouns.OPTION)} ${
      strings.acc_for_policy_choice
    } ${formatDestination(destination)} ${
      strings.acc_for
    } ${formatNumberInHebrew(
      parseInt(passengers, 10),
      HebrowNouns.PASSENGER
    )} ${formatDateRange(dates)}`;
  };

  return (
    <div className={styles.cardItem}>
      <UIRadioBtn
        ariaLabel={policyAriaLabel(index + 1, destination, passengers, dates)}
        checked={isChecked}
        onChange={handleCheckboxChange}
        name="policy"
        value={index.toString()}
      />
      <div className={styles.itemWrapper}>
        <div className={styles.expirationDetails}>
          {!isMobile && (
            <UIText
              text={strings.t_choose_policy_destination}
              size={14}
              weight={400}
              style={styles.label}
            />
          )}
          <Tooltip title={destination} placement="top" mouseEnterDelay={0.1} overlayClassName={styles.tooltipOverlay}>
            <div className={styles.tooltipTrigger}>
              <UIText
                text={truncateText(destination, isMobile ? 27 : 15)}
                size={18}
                weight={500}
              />
            </div>
          </Tooltip>
        </div>
        <div className={styles.expirationDetails}>
          {!isMobile && (
            <>
              <UIText
                text={strings.t_choose_passengers}
                size={14}
                weight={400}
                style={styles.label}
              />
              <UIText text={passengers} size={18} weight={500} />
            </>
          )}

          {isMobile && (
            <>
              <UIText
                text={passengers}
                size={16}
                weight={400}
                style={classNames(styles.label, styles.spacing)}
              />
              <UIText
                text={`${strings.t_choose_passengers},`}
                size={16}
                weight={400}
                style={classNames(styles.label, styles.spacing)}
              />

              <UIText
                text={dates}
                size={16}
                weight={400}
                style={classNames(styles.label, styles.spacing)}
              />
            </>
          )}
        </div>
        <div className={styles.expirationDetails}>
          {!isMobile && (
            <>
              <UIText
                text={strings.t_choose_policy_dates}
                size={14}
                weight={400}
                style={styles.label}
              />
              <UIText text={dates} size={18} weight={500} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyItem;
